import {Button, ColorSchemeType, FormControl, FormField, Tag} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';
import {UseFormSetValue, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {
  GetVehicleAdAttributesApiRes,
  MobileDeRefdataOptionsResponseBody,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n from '@dms/i18n';
import {VehicleCustomFields} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {HIGHLIGHT_MAX_LENGTH} from '../constants/highlightMaxLength';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';

interface RefdataOptionsQueryResult {
  data?: MobileDeRefdataOptionsResponseBody;
  isError: boolean;
  error?: ApiError;
  isFetching: boolean;
}

interface AdServerAttributeSectionProps extends TestIdProps {
  vehicleId: string;
  control: FormControl<GeneralSettingsFormValues>;
  setValue: UseFormSetValue<GeneralSettingsFormValues>;
  attributes: GetVehicleAdAttributesApiRes | undefined;
  enabledPlatformCodes: Set<AdvertisingPlatformCodeEnum>;
  isModelRangeVisible: boolean;
  isTrimLineVisible: boolean;
  modelRangeOptionsQuery: RefdataOptionsQueryResult;
  trimLineOptionsQuery: RefdataOptionsQueryResult;
  versionOptions: Array<{value: string; label: string}>;
  generationOptions: Array<{value: string; label: string}>;
  isDisabled: boolean;
}

type AttrCell = {key: string; node: ReactNode};
type AttrRow = {key: string; cells: AttrCell[]};
type AttrGroup = {
  key: string;
  platforms: ReadonlyArray<AdvertisingPlatformCodeEnum>;
  rows: AttrRow[];
};

// eslint-disable-next-line complexity -- Pre-existing violation
export function AdServerAttributeSection(props: AdServerAttributeSectionProps) {
  const theme = useTheme();
  const versionPlatforms = props.attributes?.version ?? [];
  const generationPlatforms = props.attributes?.generation ?? [];
  const highlightsPlatforms = props.attributes?.highlights ?? [];
  const modelRangePlatforms = props.attributes?.modelRange ?? [];
  const trimLinePlatforms = props.attributes?.trimLine ?? [];

  const versionVisible = versionPlatforms.some((p) => props.enabledPlatformCodes.has(p));
  const generationVisible = generationPlatforms.some((p) => props.enabledPlatformCodes.has(p));
  const highlightsVisible = highlightsPlatforms.some((p) => props.enabledPlatformCodes.has(p));
  const modelRangeVisible = props.isModelRangeVisible;
  const trimLineVisible = props.isTrimLineVisible;

  const savedModelRange = useWatch({control: props.control, name: 'modelRange'});
  const savedTrimLine = useWatch({control: props.control, name: 'trimLine'});

  const {
    data: modelRangeOptionsData,
    isError: modelRangeOptionsError,
    error: modelRangeError,
    isFetching: modelRangeOptionsFetching,
  } = props.modelRangeOptionsQuery;
  const {
    data: trimLineOptionsData,
    isError: trimLineOptionsError,
    error: trimLineError,
    isFetching: trimLineOptionsFetching,
  } = props.trimLineOptionsQuery;
  const modelRangeSuggestion = modelRangeOptionsData?.suggestion ?? null;
  const trimLineSuggestion = trimLineOptionsData?.suggestion ?? null;

  // T20-123039 US 1 AC #4.3 / US 3 AC #10 — pre-condition / fetch failures.
  // BE returns HTTP 400 with `errors[].code` (DMS-SV-AD-0051..0054); raw mobile.de
  // exception text is logged server-side and never surfaced to the FE.
  const modelRangeErrorMessage = toErrorMessage(modelRangeError);
  const trimLineErrorMessage = toErrorMessage(trimLineError);

  // T20-123039 US 1 AC #7 — help text shows the Vehicle.modelRange (Baureihe) value as-is.
  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: !modelRangeVisible}
  );
  const vehicleModelRange = vehicle?.customFields?.find(
    (f) => f.definitionId === VehicleCustomFields.modelRange.definitionId
  )?.value?.value;
  const modelRangeHelpText = isNotNil(vehicleModelRange)
    ? i18n.t('page.advertisement.adServerAttributes.modelRangeOnVehicle', {
        value: vehicleModelRange,
      })
    : i18n.t('page.advertisement.adServerAttributes.modelRangeOnVehicleEmpty');

  const nothingVisible =
    !versionVisible &&
    !generationVisible &&
    !highlightsVisible &&
    !modelRangeVisible &&
    !trimLineVisible;

  if (nothingVisible) {
    return null;
  }

  const groups: AttrGroup[] = [];

  const pushCell = (
    platforms: ReadonlyArray<AdvertisingPlatformCodeEnum>,
    rowKey: string,
    cellKey: string,
    node: ReactNode
  ) => {
    const key = [...platforms].sort().join('|');
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {key, platforms, rows: []};
      groups.push(group);
    }
    let row = group.rows.find((r) => r.key === rowKey);
    if (!row) {
      row = {key: rowKey, cells: []};
      group.rows.push(row);
    }
    row.cells.push({key: cellKey, node});
  };

  if (versionVisible) {
    pushCell(
      versionPlatforms,
      'singletons',
      'version',
      <FormField
        control={props.control}
        label={i18n.t('page.advertisement.labels.version')}
        tooltip={i18n.t('page.advertisement.labels.versionTooltip')}
        name="version"
        type="choice"
        options={props.versionOptions}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('version', props)}
      />
    );
  }

  if (generationVisible) {
    pushCell(
      generationPlatforms,
      'singletons',
      'generation',
      <FormField
        control={props.control}
        label={i18n.t('page.advertisement.labels.generation')}
        tooltip={i18n.t('page.advertisement.labels.generationTooltip')}
        name="generation"
        type="choice"
        options={props.generationOptions}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('generation', props)}
      />
    );
  }

  if (highlightsVisible) {
    ([1, 2, 3] as const).forEach((n) => {
      pushCell(
        highlightsPlatforms,
        'highlights',
        `highlight${n}`,
        <FormField
          control={props.control}
          label={i18n.t('page.advertisement.labels.highlight', {n})}
          name={`highlight${n}`}
          type="text"
          maxLength={HIGHLIGHT_MAX_LENGTH}
          isCounterVisible
          isDisabled={props.isDisabled}
          data-testid={suffixTestId(`highlight${n}`, props)}
        />
      );
    });
  }

  if (modelRangeVisible) {
    pushCell(
      modelRangePlatforms,
      'singletons',
      'modelRange',
      <VStack spacing={1}>
        <FormField
          control={props.control}
          label={i18n.t('page.advertisement.labels.baureihe')}
          tooltip={modelRangeHelpText}
          name="modelRange"
          type="choice"
          options={(modelRangeOptionsData?.options ?? []).map((opt) => ({
            value: opt,
            label: opt,
          }))}
          isDisabled={props.isDisabled || modelRangeOptionsError}
          isLoading={modelRangeOptionsFetching}
          data-testid={suffixTestId('modelRange', props)}
        />
        <Show when={isNotNil(modelRangeSuggestion)}>
          <HStack>
            {savedModelRange === modelRangeSuggestion ? (
              <Text size="xSmall" color="secondary">
                {i18n.t('page.advertisement.adServerAttributes.suggestionApplied')}
              </Text>
            ) : (
              <Button
                variant="link"
                size="small"
                title={i18n.t('page.advertisement.adServerAttributes.applySuggestion', {
                  value: modelRangeSuggestion,
                })}
                onClick={() =>
                  props.setValue('modelRange', modelRangeSuggestion, {shouldDirty: true})
                }
                isDisabled={props.isDisabled}
                data-testid={suffixTestId('modelRange-applySuggestion', props)}
              />
            )}
          </HStack>
        </Show>
        <Show when={modelRangeOptionsError}>
          <Text size="xSmall" color="warning">
            {modelRangeErrorMessage}
          </Text>
        </Show>
      </VStack>
    );
  }

  if (trimLineVisible) {
    pushCell(
      trimLinePlatforms,
      'singletons',
      'trimLine',
      <VStack spacing={1}>
        <FormField
          control={props.control}
          label={i18n.t('page.advertisement.labels.trimLine')}
          name="trimLine"
          type="choice"
          options={(trimLineOptionsData?.options ?? []).map((opt) => ({
            value: opt,
            label: opt,
          }))}
          isDisabled={props.isDisabled || trimLineOptionsError}
          isLoading={trimLineOptionsFetching}
          data-testid={suffixTestId('trimLine', props)}
        />
        <Show when={isNotNil(trimLineSuggestion)}>
          <HStack>
            {savedTrimLine === trimLineSuggestion ? (
              <Text size="xSmall" color="secondary">
                {i18n.t('page.advertisement.adServerAttributes.suggestionApplied')}
              </Text>
            ) : (
              <Button
                variant="link"
                size="small"
                title={i18n.t('page.advertisement.adServerAttributes.applySuggestion', {
                  value: trimLineSuggestion,
                })}
                onClick={() => props.setValue('trimLine', trimLineSuggestion, {shouldDirty: true})}
                isDisabled={props.isDisabled}
                data-testid={suffixTestId('trimLine-applySuggestion', props)}
              />
            )}
          </HStack>
        </Show>
        <Show when={trimLineOptionsError}>
          <Text size="xSmall" color="warning">
            {trimLineErrorMessage}
          </Text>
        </Show>
      </VStack>
    );
  }

  return (
    <VStack spacing={4}>
      {groups.map((group) => (
        <VStack key={group.key} spacing={3}>
          <HStack spacing={2} align="center">
            {group.platforms.map((code) => (
              <Tag
                key={code}
                label={code}
                colorScheme={getPlatformColor(code)}
                size="small"
                data-testid={suffixTestId(`adAttrBadge-${group.key}-${code}`, props)}
              />
            ))}
            <div
              aria-hidden
              css={css`
                flex-grow: 1;
                height: 1px;
                background-color: ${theme.colors.general.separator};
              `}
            />
          </HStack>
          <VStack spacing={4}>
            {group.rows.map((row) => (
              <Grid key={row.key} columns={[1, 2, 4]} spacing={4}>
                {row.cells.map((cell) => (
                  <div key={cell.key}>{cell.node}</div>
                ))}
              </Grid>
            ))}
          </VStack>
        </VStack>
      ))}
    </VStack>
  );
}

function getPlatformColor(code: AdvertisingPlatformCodeEnum): ColorSchemeType {
  return match<AdvertisingPlatformCodeEnum, ColorSchemeType>(code)
    .with(AdvertisingPlatformCodeEnum.MOBILE_DE, () => 'orange')
    .with(AdvertisingPlatformCodeEnum.OTOMOTO, () => 'red')
    .with(AdvertisingPlatformCodeEnum.SAUTO, () => 'red')
    .with(AdvertisingPlatformCodeEnum.TIPCARS, () => 'neutral')
    .otherwise(() => 'neutral');
}

function toErrorMessage(error: ApiError | undefined): string {
  if (!error) {
    return '';
  }
  const message = getApiErrorMessages(error);
  return Array.isArray(message) ? message.join(' ') : message;
}
